import type { DateTime } from "luxon";
import { type FC, useMemo } from "react";
import { RecurringShiftComponentSource } from "../../../../components/RecurringShift/RecurringShift";
import { RecurringShiftPanelWrapper } from "../../../../components/RecurringShift/RecurringShiftPanelWrapper";
import { useChosenDepartment } from "../../../../contexts/ChosenDepartment";
import { useDepartmentFlags } from "../../../../hooks/useDepartmentFlags";
import { resolveLocationForCoverageRow } from "../LocationConfigEditor/resolveLocationForCoverageRow";
import type { CoverageRow } from "../types";

/** Coverage request: `edit` a clicked nurse's rotation, or `create` from a +Add open-slot. */
export type RecurringShiftEditorRequest = { mode: "edit"; row: CoverageRow; nurseId: number } | { mode: "create"; row: CoverageRow };

type Props = {
	request: RecurringShiftEditorRequest;
	clickedDate: DateTime;
	onCoverageRefresh: () => unknown;
	onClose: () => void;
};

/** Bridges a Coverage click into the shared recurring panel (the same editor MS v2 / Profile use). */
export const CoverageRecurringShiftEditor: FC<Props> = ({ request, clickedDate, onCoverageRefresh, onClose }) => {
	const { chosenDepartment } = useChosenDepartment();
	const { isMedicalDepartment } = useDepartmentFlags();
	const isEdit = request.mode === "edit";

	// Create seeds the panel's first shift from the clicked row's location + shift window.
	const clickedLocation = useMemo(
		() => (isEdit ? undefined : (resolveLocationForCoverageRow(request.row, chosenDepartment.locations) ?? undefined)),
		[isEdit, request.row, chosenDepartment.locations],
	);

	return (
		<RecurringShiftPanelWrapper
			renderWithoutModal={false}
			isSynergyMS={isMedicalDepartment}
			source={RecurringShiftComponentSource.MasterSchedule}
			forceNewPanel
			clickedDate={clickedDate}
			clickedNurseId={isEdit ? request.nurseId : undefined}
			clickedLocation={clickedLocation}
			shouldAddNewShiftOnOpen={!isEdit}
			onClose={(didUpdateOrCreate) => {
				if (didUpdateOrCreate) void onCoverageRefresh();
				onClose();
			}}
		/>
	);
};
